import type { ReactNode } from 'react';
import {
  Button,
  Card,
  Spinner,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { useNavigate } from 'react-router';

import { canAccessPortal } from '../auth/portalAccess';
import { useAuth } from '../contexts/AuthContext';

interface PortalGuardProps {
  children: ReactNode;
}

const useStyles = makeStyles({
  stateRoot: {
    minHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
  },
  stateCard: {
    width: '100%',
    maxWidth: '480px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
  },
});

function getStringClaim(claim: unknown): string | undefined {
  return typeof claim === 'string' && claim.trim() ? claim : undefined;
}

function getStringArrayClaim(claim: unknown): string[] | undefined {
  if (!Array.isArray(claim)) {
    return undefined;
  }

  return claim.filter((value): value is string => typeof value === 'string');
}

export default function PortalGuard({ children }: PortalGuardProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const styles = useStyles();
  const claims = (auth.azureAccount?.idTokenClaims ?? {}) as Record<string, unknown>;
  // Raw Entra roles only: the MSAL id-token claim, or the server-validated token roles that /api/me
  // echoes back (auth.roles, needed under Teams SSO where there is no MSAL account). The effective
  // customer-tenant role (auth.user.tenantRole) is deliberately never consulted here: an app-managed
  // TenantAdmin must not reach the HostingPortal without the raw QuickDial.Admin role.
  const roles = getStringArrayClaim(claims.roles) ?? auth.roles ?? [];
  const tenantId = getStringClaim(claims.tid) ?? auth.user?.tenantId;
  const hasAccessSignal = Boolean(tenantId || roles.length > 0);

  if (auth.isLoading && !hasAccessSignal) {
    return (
      <div className={styles.stateRoot}>
        <Card className={styles.stateCard}>
          <Spinner size="large" label="Loading portal access..." />
        </Card>
      </div>
    );
  }

  if (!canAccessPortal({ tenantId, roles }, import.meta.env.VITE_HOSTING_ADMIN_TENANT_ID)) {
    return (
      <div className={styles.stateRoot}>
        <Card className={styles.stateCard}>
          <Text as="h2" size={600} weight="semibold">
            Portal access required
          </Text>
          <Text>
            You do not have access to this area.
          </Text>
          <Button appearance="secondary" onClick={() => navigate('/')}>
            Back to QuickDial
          </Button>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}